import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { MapPin, Plus, Pencil, Trash2, Star } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { formatPhoneID, digitsOnly } from "@/lib/format";
import AddressCascade from "@/components/AddressCascade";

const PHONE_MAX_DIGITS = 14;

const emptyForm = { label: "", recipient_name: "", phone: "", address_detail: "" };
const emptyCascade = {
  province_code: "", province: "", regency_code: "", regency: "",
  district_code: "", district: "", village_code: "", village: "", postal_code: "",
};

export default function AddressBook() {
  const { t } = useLang();
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | address object
  const [form, setForm] = useState(emptyForm);
  const [cascade, setCascade] = useState(emptyCascade);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api.get("/customers/me").then((r) => setAddresses(r.data.addresses || [])).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(emptyForm);
    setCascade(emptyCascade);
    setEditing("new");
  };

  const openEdit = (a) => {
    setForm({ label: a.label, recipient_name: a.recipient_name, phone: a.phone, address_detail: a.address_detail });
    setCascade({
      province_code: a.province_code, province: a.province,
      regency_code: a.regency_code, regency: a.regency,
      district_code: a.district_code, district: a.district,
      village_code: a.village_code, village: a.village,
      postal_code: a.postal_code,
    });
    setEditing(a);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!cascade.village_code || !cascade.postal_code) {
      toast.error(t("checkout.addressIncomplete"));
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, label: form.label || "Rumah", ...cascade };
      if (editing === "new") await api.post("/customers/me/addresses", payload);
      else await api.put(`/customers/me/addresses/${editing.address_id}`, payload);
      toast.success(t("account.addressSaved"));
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    try {
      await api.delete(`/customers/me/addresses/${a.address_id}`);
      toast.success(t("account.addressDeleted"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const makeDefault = async (a) => {
    try {
      await api.patch(`/customers/me/addresses/${a.address_id}/default`);
      toast.success(t("account.defaultUpdated"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="address-book">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display font-extrabold text-3xl tracking-tighter text-zinc-900">{t("account.savedAddresses")}</h1>
        <Button data-testid="add-address-btn" onClick={openNew} className="rounded-full bg-brand hover:bg-brand-hover text-white px-5">
          <Plus className="w-4 h-4 mr-1.5" /> {t("account.addAddress")}
        </Button>
      </div>

      {loading ? (
        <div className="text-sm text-zinc-500">{t("account.loading")}</div>
      ) : addresses.length === 0 ? (
        <div className="bg-white rounded-3xl border border-zinc-100 p-10 text-center">
          <MapPin className="w-8 h-8 text-zinc-300 mx-auto" />
          <div className="mt-3 font-semibold text-zinc-900">{t("account.noAddresses")}</div>
          <div className="mt-1 text-sm text-zinc-500">{t("account.noAddressesHint")}</div>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {addresses.map((a) => (
            <div key={a.address_id} data-testid={`address-card-${a.address_id}`} className="bg-white rounded-3xl border border-zinc-100 p-6 flex flex-col">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-zinc-500">{a.label}</span>
                {a.is_default && (
                  <span data-testid="default-address-badge" className="text-[10px] font-bold uppercase tracking-[0.15em] px-2 py-0.5 rounded-full bg-brand text-white">{t("account.default")}</span>
                )}
              </div>
              <div className="font-semibold text-zinc-900">{a.recipient_name}</div>
              <div className="text-sm text-zinc-600 mt-1">
                {a.address_detail}, {a.village}, {a.district}, {a.regency}, {a.province} {a.postal_code}
              </div>
              <div className="text-sm text-zinc-500 mt-1">{formatPhoneID(a.phone)}</div>
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-zinc-100">
                <button data-testid={`edit-address-${a.address_id}`} onClick={() => openEdit(a)} className="flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-brand">
                  <Pencil className="w-3.5 h-3.5" /> {t("account.editAddress")}
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button data-testid={`delete-address-${a.address_id}`} className="flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-red-600">
                      <Trash2 className="w-3.5 h-3.5" /> {t("account.deleteAddress")}
                    </button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-white">
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t("account.deleteAddress")} — {a.label}?</AlertDialogTitle>
                      <AlertDialogDescription>{t("account.deleteConfirm")}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel data-testid="delete-address-cancel">{t("account.cancel")}</AlertDialogCancel>
                      <AlertDialogAction data-testid="delete-address-confirm" onClick={() => remove(a)} className="bg-red-600 hover:bg-red-700 text-white">
                        {t("account.deleteAddress")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
                {!a.is_default && (
                  <button data-testid={`default-address-${a.address_id}`} onClick={() => makeDefault(a)} className="flex items-center gap-1 text-xs font-semibold text-zinc-700 hover:text-brand ml-auto">
                    <Star className="w-3.5 h-3.5" /> {t("account.setDefault")}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={editing !== null} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? t("account.addAddress") : t("account.editAddress")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-semibold text-zinc-800">{t("account.addressLabel")}</Label>
                <Input data-testid="address-form-label" value={form.label} onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))} placeholder={t("account.addressLabelPlaceholder")} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
              </div>
              <div>
                <Label className="text-sm font-semibold text-zinc-800">{t("account.recipientName")}</Label>
                <Input data-testid="address-form-name" value={form.recipient_name} onChange={(e) => setForm((f) => ({ ...f, recipient_name: e.target.value }))} required className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11" />
              </div>
            </div>
            <AddressCascade value={cascade} onChange={setCascade} />
            <div>
              <Label className="text-sm font-semibold text-zinc-800">{t("checkout.addressDetail")}</Label>
              <Textarea data-testid="address-form-detail" value={form.address_detail} onChange={(e) => setForm((f) => ({ ...f, address_detail: e.target.value }))} required rows={2} placeholder={t("checkout.addressDetailPlaceholder")} className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200" />
            </div>
            <div>
              <Label className="text-sm font-semibold text-zinc-800">{t("checkout.phone")}</Label>
              <Input
                data-testid="address-form-phone"
                type="tel"
                inputMode="numeric"
                value={formatPhoneID(form.phone)}
                onChange={(e) => setForm((f) => ({ ...f, phone: digitsOnly(e.target.value).slice(0, PHONE_MAX_DIGITS) }))}
                required
                placeholder="0812-3456-7890"
                className="mt-1.5 rounded-xl bg-zinc-50 border-zinc-200 h-11 w-48"
              />
            </div>
            <DialogFooter>
              <Button data-testid="address-form-save" type="submit" disabled={saving} className="rounded-full bg-brand hover:bg-brand-hover text-white px-6">
                {saving ? t("account.saving") : t("account.saveAddress")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
